import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Alert, Box, CircularProgress, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';

import { fetchRequest as fetchReceipts } from '../../redux/actions/discovery/fleetReceipts';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';

const fmtWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (e) { return '—'; }
};

const StatBox = ({ label, value, hint, accent }) => (
  <Tooltip title={hint || ''} arrow>
    <Box sx={{ cursor: hint ? 'help' : 'default' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" fontWeight={800} color={accent || 'text.primary'}>{value}</Typography>
    </Box>
  </Tooltip>
);

// Per-decile realization bars: decile 1 (highest predicted) should realize
// far more often than decile 10 — a downward staircase means the ranking works.
const DecileBars = ({ deciles }) => {
  const theme = useTheme();
  if (!deciles?.length) return null;
  const max = Math.max(...deciles.map((d) => d.rate), 0.01);
  return (
    <Stack direction="row" spacing={0.75} alignItems="flex-end" sx={{ height: 110 }}>
      {deciles.map((d) => (
        <Tooltip
          key={d.decile}
          arrow
          title={`Decile ${d.decile}${d.decile === 1 ? ' (highest predicted)' : d.decile === 10 ? ' (lowest predicted)' : ''}: ${d.realized} of ${d.n} realized (${Math.round(d.rate * 100)}%)`}
        >
          <Box sx={{ flex: 1, textAlign: 'center', cursor: 'help' }}>
            <Box
              sx={{
                height: `${Math.max(4, (d.rate / max) * 90)}px`,
                borderRadius: '3px 3px 0 0',
                bgcolor: d.decile === 1
                  ? theme.palette.primary.main
                  : alpha(theme.palette.primary.main, 0.35),
              }}
            />
            <Typography variant="caption" color="text.secondary">{d.decile}</Typography>
          </Box>
        </Tooltip>
      ))}
    </Stack>
  );
};

const FleetReceiptsPanel = ({ goalId, goal, receipts, receiptsFetch }) => {
  useEffect(() => { receiptsFetch({ goalId }); }, [goalId, receiptsFetch]);

  // A completed run may mature older runs' receipts — refresh with it.
  useRealtimeRefresh(
    eventPrefix('goal.fleet.run.completed'),
    () => receiptsFetch({ goalId }),
  );

  const data = receipts?.response;
  const isRisk = goal?.goal_type === 'rank_by_risk';
  const outcomeNoun = isRisk ? 'breached' : 'outperformed';

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <VerifiedIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={700}>
          Track record
        </Typography>
      </Stack>

      {receipts?.error && <Alert severity="error">{String(receipts.error)}</Alert>}
      {receipts?.isLoading && !data ? (
        <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={22} /></Stack>
      ) : !data || !data.evaluated_runs ? (
        <Alert severity="info">
          No matured runs yet — a run can only be verified once its full horizon
          has elapsed, so the first receipts appear one horizon after the first run.
        </Alert>
      ) : (
        <>
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <StatBox
              label="Top-decile lift"
              value={data.top_decile_lift != null ? `${data.top_decile_lift}×` : '—'}
              accent="primary.main"
              hint={`How much more often the top 10% ${outcomeNoun} than the fleet average. 1× means the ranking adds nothing; higher is better.`}
            />
            <StatBox
              label="Fleet base rate"
              value={data.base_rate != null ? `${Math.round(data.base_rate * 100)}%` : '—'}
              hint={`Share of ALL scored members that actually ${outcomeNoun}, across the evaluated runs.`}
            />
            <StatBox
              label="Runs verified"
              value={data.evaluated_runs}
              hint="Matured runs included in these numbers."
            />
          </Stack>

          <Box>
            <Typography variant="overline" color="text.secondary">
              Realization rate by predicted decile
            </Typography>
            <DecileBars deciles={data.deciles} />
          </Box>

          {Boolean(data.per_run?.length) && (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Verified</TableCell>
                    <TableCell align="right">Top decile hit rate</TableCell>
                    <TableCell align="right">Base rate</TableCell>
                    <TableCell align="right">Members</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.per_run.map((r) => (
                    <TableRow key={r.run_id} hover>
                      <TableCell>{fmtWhen(r.evaluated_at)}</TableCell>
                      <TableCell align="right">
                        {Math.round(r.top_decile_rate * 100)}%
                        <Typography component="span" variant="caption" color="text.secondary">
                          {' '}({r.top_decile_realized}/{r.top_decile_n})
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{Math.round(r.base_rate * 100)}%</TableCell>
                      <TableCell align="right">{r.n.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      )}

    </Stack>
  );
};

const mapStateToProps = (state) => ({
  receipts: state.discovery_fleet_receipts,
});

const mapDispatchToProps = (dispatch) => ({
  receiptsFetch: (payload) => dispatch(fetchReceipts(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FleetReceiptsPanel);
